import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Client } from '../../../models/Client';
import { Contact } from '../../../models/Contact';
import { Channel } from '../../../enums/channel';
import { Practice } from '../../../models/Practice';
import { UntypedFormBuilder } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../state/reducers';
import {
  getCurrentPractice,
  getCurrentPracticeSites,
} from '../../../practices/state/selectors';
import { takeWhile } from 'rxjs/operators';
import {
  CreateFormSubmission,
  GetFormToSend,
  GetMoreSendFormPatients,
  SearchForms,
  SetFormRequestClient,
  SetFormRequestContact,
} from '../../state/actions';
import {
  getCreateFormSubmissionInProgress,
  getCurrentFormToSend,
  getFormRequestChannel,
  getFormRequestClient,
  getFormRequestContact,
  getFormRequestPatients,
  IsFormRequestPatientsLoading,
} from "../../state/selectors";
import { Form } from "../../interfaces/form";
import { PageField } from "../../interfaces/page-field";
import { fieldsContainPatientInformation } from "../../../helpers/fields-contain-patient-information";
import { Patient } from "../../../models/Patient";
import { phone } from "phone";
import { getClients } from "../../../clients/state/selectors";
import { GetClientSearchResults } from "../../../clients/state/actions";
import { getUser } from "../../../auth/state/selectors";
import { User } from "../../../models/User";
import { Site } from "../../../models/Site";
import { FormSubmissionPreFilledField } from "../../interfaces/form-submission-pre-filled-field";
import { PMS } from "../../../enums/pms";
import { getConversation } from '../../../conversation/state/selectors';
import { FormRequestSteps } from '../../../enums/form-request-steps';
import { TranslateService } from '@ngx-translate/core';
import { getPermittedMessagingChannels } from '../../../helpers/get-permitted-messaging-channels';

interface ChannelOption {
  name: string;
  code: Channel;
  className: string;
  icon: string;
  inactive: boolean;
}

@Component({
  selector: 'new-form-request',
  templateUrl: './new-form-request.component.html',
  styleUrls: ['./new-form-request.component.scss'],
})
export class NewFormRequestComponent implements OnInit, OnDestroy {
  @ViewChildren('prefillInputs') prefillInputs:
    | QueryList<ElementRef>
    | undefined;
  formsubmissionPreFilleFields: Array<FormSubmissionPreFilledField> = [];
  alive = true;
  step = 0;
  createInProgress = false;
  selectedClient: Client | null = null;
  selectedContact: Contact | null = null;
  patients: Patient[] = [];
  selectedPatient: Patient | null = null;
  presetChannel: Channel | null = null;
  practice: Practice | null = null;
  channels: ChannelOption[] = [
    {
      name: Channel.WHATSAPP,
      code: Channel.WHATSAPP,
      className: Channel.WHATSAPP.toLowerCase(),
      icon: 'WhatsApp.svg',
      inactive: false,
    },
    {
      name: Channel.SMS,
      code: Channel.SMS,
      className: Channel.SMS.toLowerCase(),
      icon: 'SMS.svg',
      inactive: false,
    },
  ];
  selectedChannel?: ChannelOption;
  currentChannelOption: ChannelOption = this.channels[0];
  selectedForm: Form | null = null;
  fullForm: Form | null = null;
  fieldsToPrefill: PageField[] = [];
  formLoading = false;
  patientsLoading = false;
  patientsPage = 0;
  phoneNumber = '';
  phoneNumberIsValid = false;
  clients$ = this.store.pipe(select(getClients));
  user?: User;
  sites: Site[] = [];
  selectedSiteId = 0;
  siteError: string | null = null;
  isRegistered = false;
  showNoClientWarning = false;
  constructor(
    private store: Store<AppState>,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.subscribeToCurrentUser();
    this.subscribeToCurrentPractice();
    this.subscribeToPracticeSites();
    this.subscribeToClient();
    this.subscribeToContact();
    this.subscribeToChannel();
    this.subscribeToCurrentForm();
    this.subscribeToPatientsLoading();
    this.subscribeToPatients();
    this.subscribeToCreateFormSubmissionInProgress();
    this.isRegisteredClient();
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  subscribeToCurrentPractice(): void {
    this.store
      .pipe(select(getCurrentPractice))
      .pipe(takeWhile(() => this.alive))
      .subscribe((practice) => {
        this.practice = practice;

        if (practice) {
          this.channels = getPermittedMessagingChannels(practice);
          this.currentChannelOption = this.channels[0];
        }
      });
  }

  subscribeToCurrentUser(): void {
    this.store
      .pipe(select(getUser))
      .pipe(takeWhile(() => this.alive))
      .subscribe((user) => {
        if (user) {
          this.user = user;
        }
      });
  }

  subscribeToPracticeSites(): void {
    this.store
      .select(getCurrentPracticeSites)
      .pipe(takeWhile(() => this.alive))
      .subscribe((sites) => {
        this.sites = sites;
      });
  }

  subscribeToPatientsLoading(): void {
    this.store
      .pipe(select(IsFormRequestPatientsLoading))
      .pipe(takeWhile(() => this.alive))
      .subscribe((loading) => {
        this.patientsLoading = loading;
      });
  }

  subscribeToCreateFormSubmissionInProgress(): void {
    this.store
      .pipe(select(getCreateFormSubmissionInProgress))
      .pipe(takeWhile(() => this.alive))
      .subscribe((inProgress) => {
        this.createInProgress = inProgress;
      });
  }

  subscribeToPatients(): void {
    this.store
      .pipe(select(getFormRequestPatients))
      .pipe(takeWhile(() => this.alive))
      .subscribe((patients) => {
        this.patients = patients;
      });
  }

  subscribeToClient(): void {
    this.store
      .pipe(select(getFormRequestClient))
      .pipe(takeWhile(() => this.alive))
      .subscribe((client) => {
        this.selectedClient = client;
        this.phoneNumber = '';
        this.showNoClientWarning = false;
        this.goToNextStep();
      });

    if (!this.selectedClient) {
      this.store
        .pipe(select(getConversation))
        .pipe(takeWhile(() => this.alive))
        .subscribe((conversation) => {
          if (conversation && !conversation.client) {
            const externalUser = conversation.users
              ? conversation.users.find((user) => user.channel !== Channel.INTERNAL)
              : null;
            if (externalUser && externalUser.phone) {
              this.phoneNumber = externalUser.phone;
              this.showNoClientWarning = true;
            }
          }

        })
    }
  }

  hideNoClientWarning() {
    this.showNoClientWarning = false;
  }

  subscribeToContact(): void {
    this.store
      .pipe(select(getFormRequestContact))
      .pipe(takeWhile(() => this.alive))
      .subscribe((contact) => {
        this.selectedContact = contact;
        this.goToNextStep();
      });
  }

  subscribeToChannel(): void {
    this.store
      .pipe(select(getFormRequestChannel))
      .pipe(takeWhile(() => this.alive))
      .subscribe((channel) => {
        this.presetChannel = channel;
        this.goToNextStep();
      });
  }

  subscribeToCurrentForm(): void {
    this.store
      .pipe(select(getCurrentFormToSend))
      .pipe(takeWhile(() => this.alive))
      .subscribe((form) => {
        this.fieldsToPrefill = [];
        this.fullForm = form;

        if (form) {
          form.formPages?.forEach((page) => {
            page.pageFields
              ?.filter((field) => field.prefill || field.customizeContent)
              .forEach((prefillField) => {
                this.fieldsToPrefill.push(prefillField);
              });
          });

          this.formLoading = false;

          this.goToNextStep();
        }
      });
  }

  isRegisteredClient(): void {
    if (this.selectedClient !== null) {
      this.selectClient(this.selectedClient);
      this.showNoClientWarning = false;
    }
  }

  selectClient(client: Client): void {
    this.store.dispatch(SetFormRequestClient({ client }));
    this.isRegistered = true;
  }

  selectContact(contact: Contact): void {
    this.store.dispatch(SetFormRequestContact({ contact }));
  }

  private showPatientSelector(): boolean {
    const hasPatientSlectorInPrefill = fieldsContainPatientInformation(this.fieldsToPrefill);
    if (this.selectedForm?.saveAgainst === 'patient' || hasPatientSlectorInPrefill) {
      return true;
    }
    return false;
  }

  goToNextStep(): void {
    let step = FormRequestSteps.SEARCH_CLIENT;

    if (this.selectedClient) {
      step = FormRequestSteps.SELECT_CONTACT;
    }

    if (this.selectedClient && this.selectedContact) {
      step = FormRequestSteps.SELECT_CHANNEL;
    }

    if (
      this.selectedClient &&
      this.selectedContact &&
      (this.presetChannel || this.selectedChannel)
    ) {
      step = FormRequestSteps.SELECT_FORM;
    }

    if (this.selectedContact && this.presetChannel) {
      step = FormRequestSteps.SELECT_FORM;
    }

    if (
      this.selectedClient &&
      this.selectedContact &&
      (this.presetChannel || this.selectedChannel) &&
      this.selectedForm
      && this.showPatientSelector()
    ) {
      step = FormRequestSteps.SELECT_PATIENT;
    }

    if (
      this.selectedClient &&
      this.selectedContact &&
      (this.presetChannel || this.selectedChannel) &&
      this.selectedForm &&
      (
        this.selectedPatient ||
        (
          this.practice && this.practice.pms !== PMS.TELEOS && // For Teleos patient is required to store completed forms on PMS
          !this.formLoading &&
          !fieldsContainPatientInformation(this.fieldsToPrefill)
        )
      )
    ) {
      if (this.fieldsToPrefill.length === 0) {
        if (!this.selectedPatient && this.showPatientSelector()) {
          step = FormRequestSteps.SELECT_PATIENT;
        } else if (this.selectedForm.takePayment) {
          step = FormRequestSteps.UPDATE_PAYMENT_INFO;
        } else if (!this.selectedForm.takePayment && this.sites.length > 0) {
          step = FormRequestSteps.SELECT_SITE;
        } else if (this.selectedPatient) {
          this.sendForm();
        }
      } else {
        if (!this.selectedPatient && this.showPatientSelector()) {
          step = FormRequestSteps.SELECT_PATIENT;
        } else {
          step = FormRequestSteps.PREFILL_FIELDS;
        }
      }
    }

    if (this.selectedPatient) {
      if (this.selectedForm && this.selectedForm.takePayment && this.fieldsToPrefill.length === 0) {
        step = FormRequestSteps.UPDATE_PAYMENT_INFO;
      } else if (this.selectedForm && this.selectedForm.takePayment && this.fieldsToPrefill.length > 0) {
        step = FormRequestSteps.PREFILL_FIELDS;
      } else if (this.sites.length > 0 && this.fieldsToPrefill.length === 0) {
        step = FormRequestSteps.SELECT_SITE;
      } else if (this.sites.length > 0 && this.fieldsToPrefill.length > 0) {
        step = FormRequestSteps.PREFILL_FIELDS;
      }
    } else if (this.selectedForm && this.selectedClient && this.showPatientSelector()) {
      step = FormRequestSteps.SELECT_PATIENT;
    } else if (this.selectedForm && this.fieldsToPrefill.length > 0) {
      step = FormRequestSteps.PREFILL_FIELDS;
    } else if (this.selectedForm && this.selectedForm.takePayment) {
      step = FormRequestSteps.UPDATE_PAYMENT_INFO;
    } else if (this.selectedForm && !this.selectedForm.takePayment && this.sites.length > 0) {
      step = FormRequestSteps.SELECT_SITE;
    }

    if (this.phoneNumber && this.selectedForm && this.practice) {
      if (this.fieldsToPrefill.length > 0) {
        step = FormRequestSteps.PREFILL_FIELDS;
      } else if (this.selectedForm.takePayment && this.sites.length > 0) {
        step = FormRequestSteps.UPDATE_PAYMENT_INFO;
      } else if (!this.selectedForm.takePayment && this.sites.length > 0) {
        step = FormRequestSteps.SELECT_SITE;
      }
    }

    if (step === FormRequestSteps.PREFILL_FIELDS && this.sites.length === 1) {
      this.selectedSiteId = Number(this.sites[0].id);
    }
    // In case practice has one or zero sites and step is SELECT_SITE then submit the form
    if (step === FormRequestSteps.SELECT_SITE && this.sites.length <= 1) {
      if (this.sites.length === 1) {
        this.selectedSiteId = Number(this.sites[0].id);
      }

      this.sendForm();
    }

    // In case practice has one or zero sites and step is UPDATE_PAYMENT_INFO then set selected site
    if (step === FormRequestSteps.UPDATE_PAYMENT_INFO && this.sites.length <= 1 && this.selectedForm?.takePayment) {
      this.selectedSiteId = Number(this.sites[0].id);
    }

    // In case no prefill no sites and no payment then submit the form
    if (step === FormRequestSteps.SELECT_FORM) {
      this.selectedSiteId = 0;
      this.sendForm();
    }

    this.step = step;
  }

  goBackTo(step: number): void {
    if (step === 0) {
      this.store.dispatch(SetFormRequestClient({}));
    }

    if (step === 1) {
      this.store.dispatch(SetFormRequestContact({}));
    }

    if (step === 2) {
      if (this.presetChannel && this.selectedChannel) {
        // go back past channel step if channel is preset
        this.goBackTo(1);
      } else {
        this.selectedChannel = undefined;
        this.step = 2;
      }
    }

    if (step === 3) {
      this.selectedForm = null;
      this.fullForm = null;
      this.step = 3;
    }

    if (step === 4) {
      this.selectedPatient = null;
      if (!this.showPatientSelector()) {
        this.goBackTo(3);
      } else {
        this.step = 4;
      }
    }

    if (step === 5) {
      if (this.fieldsToPrefill.length === 0) {
        this.goBackTo(4);
      } else {
        this.step = 5;
      }
    }

    if (step === 6) {
      if (this.selectedForm) {
        this.goBackTo(6);
      }
    }
  }

  goToFormStep(): void {
    this.selectedChannel = this.currentChannelOption;
    this.step = 3;
  }

  handleFormSelected(form: Form): void {
    this.selectedForm = form;
    this.getFullForm();
  }

  getFullForm(): void {
    if (this.selectedForm) {
      this.formLoading = true;
      this.store.dispatch(GetFormToSend({ formId: this.selectedForm.id }));
    }
  }

  sendForm(): void {
    this.siteError = null;

    const selectedSiteIdNumber = Number(this.selectedSiteId);

    if (this.formsubmissionPreFilleFields.length === 0) {
      this.setPrefilledForm();
    }
    if (
      this.selectedForm &&
      this.practice &&
      this.selectedClient &&
      this.selectedContact
    ) {
      if (
        selectedSiteIdNumber === 0 &&
        this.sites.length > 0
      ) {
        this.siteError = this.translateService.instant('forms.components.new_request.site_error');
        return;
      }

      this.store.dispatch(
        CreateFormSubmission({
          dto: {
            formId: this.selectedForm.id.toString(),
            practiceId: this.practice.coreId,
            ownerId: this.user?.id || '0',
            patientPmsId: this.selectedPatient?.pmsId || null,
            vbResourceId:
              this.sites.find(
                (site) => site.id.toString() === this.selectedSiteId.toString()
              )?.vetbookerResourceId || null,
            practiceCountry: this.practice.country,
            clientPmsId:
              this.selectedClient.pmsId ?? this.selectedClient.pmsDisplayId,
            contactNumber: this.selectedContact.value,
            data: this.formsubmissionPreFilleFields.length
              ? this.formsubmissionPreFilleFields.map(
                (formsubmissionPreFilleField) => {
                  const matchingPrefill = this.fieldsToPrefill.find(
                    (field) =>
                      field.id.toString() ===
                      formsubmissionPreFilleField.fieldId.toString()
                  );
                  return {
                    fieldId: formsubmissionPreFilleField.fieldId,
                    fieldLabel: matchingPrefill?.label || '',
                    fieldValue: formsubmissionPreFilleField.fieldValue,
                  };
                }
              )
              : [],
          },
          client: this.selectedClient,
          contact: this.selectedContact,
          channel:
            this.presetChannel ||
            this.selectedChannel?.code ||
            Channel.WHATSAPP360,
        })
      );
    } else if (this.phoneNumber && this.selectedForm && this.practice) {
      this.store.dispatch(
        CreateFormSubmission({
          dto: {
            formId: this.selectedForm.id.toString(),
            practiceId: this.practice.coreId,
            ownerId: this.user?.id || '0',
            patientPmsId: this.selectedPatient?.pmsId || null,
            vbResourceId:
              this.sites.find(
                (site) => site.id.toString() === this.selectedSiteId.toString()
              )?.vetbookerResourceId || null,
            practiceCountry: this.practice.country,
            clientPmsId: null,
            contactNumber: this.phoneNumber,
            data: this.formsubmissionPreFilleFields.length
              ? this.formsubmissionPreFilleFields.map(
                (formsubmissionPreFilleField) => {
                  const matchingPrefill = this.fieldsToPrefill.find(
                    (field) =>
                      field.id.toString() ===
                      formsubmissionPreFilleField.fieldId.toString()
                  );
                  return {
                    fieldId: formsubmissionPreFilleField.fieldId,
                    fieldLabel: matchingPrefill?.label || '',
                    fieldValue: formsubmissionPreFilleField.fieldValue,
                  };
                }
              )
              : [],
          },
          client: null,
          contact: {
            name: 'Mobile',
            typeCode: '0',
            value: this.phoneNumber,
            facebookLinked: false,
            instagramLinked: false,
          },
          channel:
            this.presetChannel ||
            this.selectedChannel?.code ||
            Channel.WHATSAPP360,
        })
      );
    }
  }

  onSiteIdChange(newSiteId: string): void {
    this.siteError = null;
  }

  selectPatient(patient: Patient): void {
    this.selectedPatient = patient;
    this.goToNextStep();
  }

  handleMorePatients(): void {
    this.patientsPage++;
    this.store.dispatch(GetMoreSendFormPatients({ page: this.patientsPage }));
  }

  validatePhoneInput(): void {
    let phoneResult = phone(this.phoneNumber);
    if (!phoneResult.isValid) {
      phoneResult = phone(this.phoneNumber, {
        country: this.practice?.country,
      });
    }

    this.phoneNumberIsValid = !!phoneResult.isValid;
  }

  validateKeyPress($event: KeyboardEvent): void {
    const regex = new RegExp('^[a-zA-Z]+$');
    const key = $event.key;

    if (
      key.length === 1 &&
      !$event.ctrlKey &&
      !$event.metaKey &&
      regex.test(key)
    ) {
      $event.preventDefault();
    }
  }

  usePhoneNumber(): void {
    this.showNoClientWarning = true;
    let phoneResult = phone(this.phoneNumber, {
      country: this.practice?.country,
    });
    if (!phoneResult.isValid) {
      phoneResult = phone(this.phoneNumber);
    }
    if (!phoneResult.isValid || !phoneResult.phoneNumber) {
      this.phoneNumberIsValid = false;
      return;
    }
    this.goBackTo(2);
  }

  setPrefilledForm(): void {
    if (this.prefillInputs) {

      if (!this.formsubmissionPreFilleFields) {
        this.formsubmissionPreFilleFields = [];
      }

      const newFields = this.prefillInputs.map((prefillInput) => ({
        fieldId: prefillInput.nativeElement.name,
        fieldValue: prefillInput.nativeElement.value,
      }));

      this.formsubmissionPreFilleFields.push(...newFields);

    }
  }


  goToSiteStep(): void {
    this.setPrefilledForm();
    if (this.sites.length > 0 && this.sites.length === 1) {
      this.selectedSiteId = Number(this.sites[0].id);
      this.sendForm();
    }
    this.step = FormRequestSteps.SELECT_SITE;
  }

  goToPaymentStep(): void {
    this.setPrefilledForm();
    this.step = FormRequestSteps.UPDATE_PAYMENT_INFO;
  }
}
